---
title: Modul pogleda FOI
sidebar_position: 3
description: Modul pogleda FOI
---

# Modul pogleda FOI – podrobna analiza

**Modul pogleda FOI** je glavni delovni prostor za analizo Območij zanimanja. Ponuja celovit nabor gradnikov in orodij za pregledovanje satelitskih posnetkov, signalov, markerjev in atributov polja.

## Pregled postavitve pogleda FOI

Pogled FOI je organiziran v tri glavne stolpce in spodnje panele:

- **Levi stolpec**: Seznam/tabela FOI omogoča hitro premikanje med Območji zanimanja, ne da bi zapustili pogled podrobne analize.
- **Srednji stolpec**: Gradnika Opazovanja in Zemljevid za analizo posnetkov
- **Desni stolpec**: Zavihka Atributi FOI in Markerji
- **Spodnji paneli**: Gradnika Vizualizacija signalov in markerjev ter Timelapse

**Opomba**: Funkcije seznama FOI so opisane v poglavju [Gradnik seznam/tabela FOI](02-getting-started.mdx#gradnik-seznamatabele-območij-zanimanja). Za premikanje po zemljevidu, funkcije gradnika in funkcijo Pojdi na glejte [Gradnik zemljevida in navigacija](02-getting-started.mdx#zemljevidni-gradnik-in-navigacija).

![Celotna postavitev pogleda FOI](../imgs/foi-view.png)

## Gradnik Opazovanja

**Gradnik Opazovanja** prikazuje izseke satelitskih posnetkov, zajete skozi čas za izbrani FOI. Vsak izsek predstavlja eno opazovanje (Observation).

### Namen

- Ogled časovnice satelitskih posnetkov za FOI
- Spremljanje sprememb pokrovnosti tal skozi kronološko zaporedje
- Prepoznavanje določenih zanimivih datumov za podrobnejši pregled

### Možnosti konfiguracije

Vse nastavitve opazovanj so dostopne prek **panela možnosti v nogi opazovanj** (ikona zobnika ali nastavitev na dnu panela opazovanj). Za te kontrolnike ni ločenega predala Nastavitve.

#### Sloj opazovanj

**Izbira sloja opazovanj**:

1. Odprite **možnosti v nogi opazovanj**
2. Poiščite spustni meni **Sloj**
3. Izberite enega ali več slojev (npr. »S2 True Color«, »S2 NDVI«, »S2 False Color«)
4. Opazovanja in posnetki na zemljevidu se posodobijo takoj

**Razpoložljivi sloji** so specifični za obseg (Scope). Pogosti primeri:

- **S2 True Color**: Posnetki v naravnih barvah
- **S2 False Color**: Izboljšana vizualizacija vegetacije
- **S2 NDVI**: Indeks zdravja vegetacije
- **S2 EVI, LAI, CCC**: Drugi vegetacijski indeksi
- **Sloji po meri**: Vizualizacije, specifične za projekt

**Načini uporabe**:

- Preklopite na NDVI za oceno zdravja vegetacije
- Uporabite True Color za naraven videz
- Uporabite False Color za poudarjanje gostote vegetacije

#### Časovno obdobje

Časovno obdobje (začetni in končni datum) je globalna nastavitev aplikacije. Odprite **stranski panel** in tam prilagodite **časovno obdobje**. Prikazana bodo le opazovanja znotraj tega obdobja. Če ne vidite nobenih posnetkov, preverite, ali se vaše časovno obdobje ujema z razpoložljivostjo podatkov.

**Izbira datuma**:

- Kliknite na opazovanje
- Nastavi se časovni žig **Izbrani datum** v splošnih nastavitvah
- Povezani gradniki (posnetki na zemljevidu, gradnik Signali in markerji, timelapse) se posodobijo in prikažejo ta določeni datum

#### Možnosti postavitve

- **Slik na vrstico**: Prilagodite, koliko opazovanj se prikaže v eni vrstici
  - Manj stolpcev = večje slike
  - Več stolpcev = več hkrati vidnih slik, a manjših
  - Onemogočeno, ko je vklopljen **Tedenski pogled**
- **Tedenski pogled**: Ko je omogočen, vsaka vrstica predstavlja en koledarski teden, kar olajša prepoznavanje tedenskih vzorcev. Ko je onemogočen, slike zapolnjujejo vrstice glede na nastavitev števila slik na vrstico.

#### Veljavni signali

**Preklop možnosti Prikaži samo veljavna opazovanja**:

- Označite **Prikaži samo veljavna opazovanja**
- Skrije slike z nizkokakovostnimi podatki (oblaki, sence, napake pri obdelavi)
- Brez oznake se prikažejo vsa razpoložljiva opazovanja

**Izbira veljavnih signalov po viru**:

- Za vsak vir slikovnih podatkov (zbirko) izberite tip veljavnega signala

#### Odmik

- Določa rob vidnega območja okoli FOI
- Večji odmik prikaže več konteksta okoli polja
- Manjši odmik se osredotoči tesno na mejo FOI

#### Prekrivanje geometrije

- Ko je omogočeno, je meja FOI izrisana čez vsak izsek opazovanja, tako da vidite, katero območje posnetek pokriva
- Izklopite ga, da vidite celoten izsek brez prekrivanja z mejo

#### Pripeti vrednosti signalov

- V razdelku **Pripeti signali** izberite tip signala za vsako zbirko, da se njegova vrednost prikaže na vsaki kartici opazovanja
- Vrednosti se prikažejo kot oznake v glavi izseka
- Pomaga hitro prepoznati trende signalov brez preklapljanja v graf
- Nastavite na **Brez**, da onemogočite pripenjanje za to zbirko

![Pripeti signali](../imgs/observations-pinned-signals.png)

### Interakcije

**Klik za povečavo**:

1. Kliknite ikono razširitve pri kateremkoli izseku slike v panelu opazovanj
2. Slika se odpre v **premakljivem oknu s spremenljivo velikostjo**
3. V pojavnem oknu:
   - Povlecite okno, da spremenite njegovo lego
   - Spremenite velikost z vogalnimi ročicami
   - Zaprite z gumbom X
4. Hkrati lahko povečate več opazovanj in jih primerjate drugo ob drugem

![Povečava opazovanj](../imgs/observations-enlarge.png)

**Premikanje po časovnici**:

- Drsajte po panelu opazovanj za prikaz vseh razpoložljivih datumov
- Slike so razvrščene kronološko

## Gradnik Atributi FOI

**Gradnik Atributi FOI** prikazuje splošne referenčne informacije o izbranem FOI.

### Namen

- Ogled metapodatkov in značilnosti FOI
- Razumevanje lastnosti polja (posevek, velikost, lokacija itd.)
- Dostop do referenčnih atributov za analizo

### Vsebina

Prikazani atributi lahko vključujejo:

- **ID FOI**: Sistemsko ustvarjen in edinstven popolnoma kvalificiran identifikator (v obliki `<SCOPE>.FOI.<ID>`)
- **Referenčni ID**: Referenčni ID (ID-ji, ki jih posreduje stranka in so edinstveni znotraj tipa FOI v obsegu; identifikator, po katerem uporabniki običajno prepoznavajo FOI)
- **Tip FOI**: Klasifikacija (Classification) FOI (polje, parcela itd.)
- **Tip posevka**: Kateri posevek je prijavljen ali zaznan
- **Raba tal**: Kmetijska kategorija
- **Površina**: Velikost polja
- **Dodatni atributi**: Metapodatki, specifični za projekt

**Opomba**: Razpoložljivi atributi so odvisni od konfiguracije obsega in virov podatkov. Natančen nabor prikazanih atributov je določen s podatki posameznega obsega, zato različni obsegi izpostavljajo različne atribute.

## Gradnik Markerji

**Gradnik Markerji** prikazuje napovedne meritve, ki kažejo vzorce upravljanja in značilnosti polja.

Markerji so izpeljani iz fenoloških vzorcev signalov in nudijo vpogled v:

- Napovedi klasifikacije posevkov
- Zaznavo gole zemlje
- Dogodke košnje
- Analizo homogenosti
- In drugo

Gradnik Markerji ima štiri zavihke: **Pregled**, **Odločitve**, **Ocene** in **Validacija** (Validation) (Validacija se prikaže samo v Validacijski konzoli).

### Zavihek Pregled

Zavihek **Pregled** nudi splošen pregled analitičnih markerjev na visoki ravni. Markerji odločitev in ocen tukaj **niso prikazani** — vsak ima svoj namenski zavihek.

![Pregled markerjev](../imgs/markers-summary.png)

**Prikazane informacije**:

1. **Klasifikacije**:
   - Napovedana kategorija posevka (npr. »Žita«, »Okopavine«)
   - Ocena zaupanja (Confidence), prikazana kot odstotek
   - Z miškinim kazalcem se pomaknite nad vrstico za prikaz namigov **»Klasificirano kot«** in **»Prijavljeno kot«**
   - Glejte [Podroben pogled klasifikacije skupine posevkov in rabe tal](#podroben-pogled-klasifikacije-skupine-posevkov-in-rabe-tal) ter [Podroben pogled markerjev Razdalja in Podobnost](#podroben-pogled-markerjev-razdalja-in-podobnost)

2. **Povprečje NDVI**:
   - Povprečna vrednost NDVI v obdobju analize
   - Kaže splošno zdravje vegetacije
   - Številčna vrednost in interpretacija

3. **Napovedana homogenost**:
   - Ocena enotnosti polja z uporabo ocene homogenosti
   - Ocena zaupanja
   - Kaže, ali je polje enotno ali mešano

4. **Opazovanja** (npr. zaznave gole zemlje):
   - **Število zaznav** znotraj časovnega obdobja
   - Datumi opazovanj
   - Pomaga pri prepoznavanju dogodkov obdelave

5. **Zaznani dogodki** (npr. košnja):
   - **Število identificiranih dogodkov**, pri čemer sta števili dogodkov naraščanja in upadanja prikazani neposredno v vrstici pregleda (npr. »2 | 1« za 2 naraščanja in 1 upadanje)
   - Datumi zaznanih dogodkov

Vsako vrstico je mogoče razširiti — kliknite vrstico za prikaz celotnega podrobnega pogleda.

#### Podroben pogled klasifikacije skupine posevkov in rabe tal

Ta podpogleda prikazujeta podrobne ocene klasifikacije.

**Funkcije**:

- **Grafična vizualizacija**: Stolpčni grafi s prikazom ocen zaupanja za vsak razred
- **Napovedana skupina je poudarjena zeleno**: Napoved z najvišjim zaupanjem
- **Prijavljena skupina (če je v nasprotju) modro**: Prikaže, ali se prijavljeni posevek razlikuje od napovedi
- **Prikaz vseh razredov**: Celovit razčlen verjetnosti klasifikacije

**Kako brati**:

1. Poiščite **zeleni stolpec** — to je napovedani razred
2. Preverite **odstotek zaupanja** — višji pomeni večjo gotovost
3. Če je prisoten **modri stolpec**, gre za prijavljeni posevek (če se razlikuje)
4. Primerjajte napovedano in prijavljeno za prepoznavanje morebitnih neskladij

#### Podroben pogled markerjev Razdalja in Podobnost

Markerja **Razdalja** in **Podobnost** oba primerjata časovno vrsto signala FOI (njegov spektralni profil skozi sezono) z referenčnimi profili vsakega kandidatnega posevka, da ocenita, kako dobro se FOI ujema s svojim prijavljenim posevkom in kateremu posevku je najbolj podoben. Uporabljata **enako postavitev stolpčnega grafa** kot pogledi klasifikacije — vendar merita ujemanje v nasprotnih smereh, zato sta njuna grafa **razvrščena nasprotno**. Vsak pogled prikazuje **klasificirani (najbolje ujemajoči se) razred z njegovo oceno** in **prijavljeni razred z njegovo oceno**; najbolje ujemajoči se razred je poudarjen **zeleno**, **prijavljeni razred pa je prikazan modro**, kadar se razlikuje od najboljšega ujemanja.

**Marker Razdalja**

- **Kaj meri**: kako spektralno _različen_ je FOI od referenčnega profila vsakega razreda (statistična razdalja). **Nižja = bližje = bolj podobno.**
- **Kako brati**: ocene so razvrščene **od najnižje**, zato je **zgornji stolpec najbližje (najboljše) ujemanje**. Nizka ocena pri **prijavljenem** razredu pomeni, da FOI izgleda kot njegov prijavljeni posevek; visoka ocena pri prijavljenem razredu pomeni, da izgleda drugače od njega.

**Marker Podobnost**

- **Kaj meri**: kako tesno se spektralni podpis FOI _ujema_ z referenčnim profilom vsakega razreda. **Višja = bolj podobno.**
- **Kako brati**: ocene so razvrščene **od najvišje**, zato je **zgornji stolpec najboljše ujemanje**. Če se prijavljeni razred ne pojavi blizu vrha, profil FOI odstopa od pričakovanega za ta posevek.

**Interpretacija**:

- Močno ujemanje s posevkom **, ki ni prijavljeni** (nizka Razdalja ali visoka Podobnost z drugim razredom), lahko nakazuje napačno klasifikacijo ali nepravilno prijavo — označite za pregled.
- Šibko ujemanje s **prijavljenim** posevkom (visoka Razdalja ali nizka Podobnost pri prijavljenem razredu) je znak za podrobnejši pregled FOI.
- **Pogosta zmeda**: Markerja izgledata enako, a se bereta na nasproten način — **nizka ocena Razdalje** pomeni dobro ujemanje, medtem ko **visoka ocena Podobnosti** pomeni dobro ujemanje.

---

### Zavihek Odločitve

Zavihek **Odločitve** prikazuje vse markerje odločitev (Decision) za izbrani FOI. Markerji odločitev uporabljajo nastavljivo odločitveno drevo, ki združuje več vhodov za oceno skladnosti s poslovnimi pravili.

![Markerji – Odločitve](../imgs/markers-decisions.png)

Markerji so prikazani s svojimi **človeško berljivimi imeni** (ne s surovimi notranjimi kodami). Vsaka vrstica prikazuje rezultat v obliki semaforja:

- **Zelena**: Odobreno / Skladno
- **Rumena**: Opozorilo / Zahteva pozornost
- **Rdeča**: Zavrnjeno / Neskladno

**Diagram odločitve**:

1. Kliknite vrstico markerja odločitve, da jo razširite
2. Prikaže se celoten diagram odločitvenega drevesa kot slika, ki vizualizira logično pot, ki je pripeljala do rezultata odločitve
3. Diagram je statičen — vozlišča nimajo interaktivnih namigov ali stanj ob pomiku miške

### Zavihek Ocene

Zavihek **Ocene** prikazuje markerje ocen (Evaluation) za izbrani FOI z logičnimi (da/ne) odgovori.

![Markerji – Ocene](../imgs/markers-evaluations.png)

Markerji so prikazani s svojimi **človeško berljivimi imeni**. Vrstic ni mogoče razširiti. Če v vrstici ni dovolj prostora za prikaz celotnega vprašanja ocene, je vprašanje skrajšano s ... in celotno vprašanje se prikaže v namigu.

## Gradnik Vizualizacija signalov in markerjev

**Gradnik Vizualizacija signalov in markerjev** prikazuje časovni graf signalov in markerjev za izbrani FOI. Kateri signali in markerji so prikazani v tem gradniku, je določeno v konfiguraciji obsega (glejte [Konfiguracija obsega](05-scope-configuration.mdx)).

![Gradnik vizualizacije signalov in markerjev](../imgs/sig-ma-viz.png)

### Namen

- Vizualizacija podatkov signalov in markerjev skozi čas
- Prepoznavanje trendov in vzorcev
- Povezovanje signalov s posnetki in markerji

### Funkcije

#### Prikaz signalov in markerjev

- Razpoložljivi in nastavljeni signali in markerji za izbrani FOI in kontekst markerjev
- Signali so prikazani kot zvezne črte
- Markerji so prikazani kot točke ali diskretni kazalniki
- Barvno označeni za lažje prepoznavanje
- **Referenčni podatki FOI** (datumski atributi, nastavljeni za posamezni obseg) se v grafu pojavijo kot **statični navpični trakovi**

#### Interaktivna legenda in kontrolniki

Panel kontrolnikov je pripet ob bok grafa (privzeto: desno). Vsebuje:

- **Barvna potrditvena polja**: Preklop vidnosti posameznega signala ali markerja v grafu
- **Namig**: S klikom na oznako signala/markerja preklopite, ali se ta pojavi v podatkovnem namigu — **krepke** oznake označujejo, da bo signal/marker prikazan v namigu
- **Meni Možnosti**: Prilagoditev umestitve namiga in premik panela kontrolnikov na drug položaj (levo, desno, zgoraj, spodaj)

#### Možnosti umestitve namiga

- **Kazalec**: Namig sledi miški
- **Zgoraj, Spodaj, Levo, Desno**: Fiksen položaj glede na graf
- **Vogali**: Zgoraj levo, zgoraj desno, spodaj levo, spodaj desno

#### Izbira datuma

- Kliknite na datum v grafu
- Nastavi se časovni žig **Izbrani datum** v splošnih nastavitvah
- Povezani gradniki (posnetki na zemljevidu, opazovanja, timelapse) se posodobijo in prikažejo ta določeni datum
- Povežite signale s prostorskimi posnetki

#### Podpora več osem (če je omogočena)

- Signalom je mogoče dodeliti različne osi Y
- Do 3 osi (leva, desna ali obe)
- Omogoča primerjavo signalov z različnimi razponi vrednosti
- Oznake in merila osi so prikazani na robovih grafa

## Gradnik Timelapse

**Gradnik Timelapse** ustvari animirano vizualizacijo opazovanj FOI skozi čas.

### Namen

- Ogled dinamičnih sprememb skozi čas v obliki animacije
- Vizualno prepoznavanje časovnih vzorcev
- Ustvarjanje deljivih animacij GIF za poročila

![Gradnik Timelapse](../imgs/timelapse.png)

### Kontrolniki

#### Predvajanje animacije

1. Kliknite gumb **Predvajaj**
2. Opazovanja se predvajajo v kronološkem zaporedju
3. Animacija se neprekinjeno ponavlja v zanki ali se ustavi na koncu (odvisno od nastavitev)
4. Kliknite **Premor**, da ustavite predvajanje

#### Premikanje po izsekih slik

- Z gumboma **Prejšnji** in **Naslednji** ročno premikajte po opazovanjih
- Pomaknite se za eno sliko naenkrat
- Pomaknite se nazaj za pregled določenih datumov

#### Hitrost predvajanja

- Prilagodite hitrost animacije
- Počasneje za podroben pregled
- Hitreje za splošni pregled

#### Izbira sloja

- Izberite, kateri sloj opazovanj animirati (enako kot pri gradniku Opazovanja)
- Timelapse odraža trenutno izbrani sloj
- Preklopite, katere geometrije prikazati (izvirna meja FOI, pikselizirana geometrija FOI)

#### Prenos animacije kot GIF

1. Kliknite gumb **Prenesi GIF**
2. Timelapse se izriše in shrani kot datoteka GIF
3. GIF uporabite v predstavitvah, poročilih ali zunanjih orodjih
